import React, { useEffect, useState, useCallback } from 'react';
import { claim_leaderboard_reward, get_leaderboard } from 'services/db-service';
import { useAuth } from '../../helpers/authContext';
import { useToast } from 'components/ui/use-toast';
import { Button } from 'reactstrap';
import steemLogo from '../../assets/img/steem.webp';

function getWeekStart(offset = 0): string {
    const now = new Date();
    const dayOfWeek = now.getUTCDay();
    const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const monday = new Date(Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate() - daysSinceMonday + offset * 7
    ));
    return monday.toISOString().slice(0, 10);
}

export const ClaimRewardBanner: React.FC = () => {
    const { user } = useAuth();
    const { toast } = useToast();
    const [rewardInfo, setRewardInfo] = useState<{ amount: number, rank: number, symbol: string } | null>(null);
    const [isClaiming, setIsClaiming] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const weekStart = getWeekStart(-1);

    const checkReward = useCallback(async () => {
        if (!user) return;
        setIsLoading(true);
        try {
            const data = await get_leaderboard(weekStart);
            const entries = data.entries || [];
            const rewardsEnabled = data.rewards_enabled || false;
            const prizeSymbol = data.prize_symbol || 'STEEM';

            const userEntry = entries.find((e: any) => e.username.toLowerCase() === user.toLowerCase());

            if (rewardsEnabled && userEntry && userEntry.reward_amount > 0 && !userEntry.is_claimed) {
                setRewardInfo({
                    amount: userEntry.reward_amount,
                    rank: userEntry.rank,
                    symbol: prizeSymbol
                });
            } else {
                setRewardInfo(null);
            }
        } catch (e) {
            console.error('[ClaimRewardBanner] check error', e);
        } finally {
            setIsLoading(false);
        }
    }, [user, weekStart]);

    useEffect(() => {
        checkReward();
    }, [checkReward]);

    const handleClaim = async () => {
        if (!user) return;
        setIsClaiming(true);
        try {
            const res = await claim_leaderboard_reward(user, weekStart);
            if (res.success) {
                toast({ title: "Reward Claimed!", description: `Transaction: ${res.tx_id}` });
                setRewardInfo(null); // Hide banner after successful claim
            } else {
                toast({ title: "Claim Failed", description: res.error, variant: "destructive" });
            }
        } catch (err) {
            toast({ title: "Error", description: "Something went wrong. Try again.", variant: "destructive" });
        } finally {
            setIsClaiming(false);
        }
    };

    if (isLoading || !rewardInfo) return null;

    return (
        <div className="lb-claim-banner animate__animated animate__fadeInUp mb-4">
            <div className="d-flex align-items-center justify-content-between p-3 bg-speem-confirm" style={{ borderRadius: '20px' }}>
                <div>
                    <h4 className="mb-0">🎉 Congratulations @{user}!</h4>
                    <p className="mb-0 small opacity-8">
                        You won <strong>{rewardInfo.amount.toFixed(3)} {rewardInfo.symbol}</strong> (Rank #{rewardInfo.rank})
                    </p>
                </div>
                <Button
                    color="speem"
                    className="px-4"
                    disabled={isClaiming}
                    onClick={handleClaim}
                    style={{ background: 'var(--speem)', color: 'white', borderRadius: '15px', position: 'relative' }}
                >
                    {isClaiming ? (
                        <i className="fas fa-spinner fa-spin mr-2" />
                    ) : (
                        <img src={steemLogo} alt="Steem" style={{ width: '18px', marginRight: '8px', filter: 'brightness(0) invert(1)' }} />
                    )}
                    Claim Prize
                </Button>
            </div>
        </div>
    );
};
